package se.mittpass.web.pages

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.*
import se.mittpass.shared.repository.*
import se.mittpass.web.UserInfo
import se.mittpass.web.authStorage
import se.mittpass.web.components.FaIcon
import se.mittpass.web.httpClient
import se.mittpass.web.style.RosePineTheme
import kotlin.math.roundToInt

private fun cafeTodayFormatDateJs(d: kotlin.js.Date): String {
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

private fun cafeTodayLocalDate(): String = cafeTodayFormatDateJs(kotlin.js.Date())

private fun cafeTodayParseDate(dateStr: String): kotlin.js.Date =
    kotlin.js.Date("${dateStr}T12:00:00")

private fun cafeTodayAddDays(dateStr: String, days: Int): String {
    val d = cafeTodayParseDate(dateStr)
    (d.asDynamic()).setDate(d.getDate() + days)
    return cafeTodayFormatDateJs(d)
}

private fun cafeTodayMondayOf(dateStr: String): String {
    val d = cafeTodayParseDate(dateStr)
    val dayOfWeek = (d.getDay() + 6) % 7
    return cafeTodayAddDays(dateStr, -dayOfWeek)
}

@Composable
fun CafeTodayPage(
    workplaceId: Long,
    userInfo: UserInfo?,
    isManager: Boolean,
    onNavigateToTab: (String) -> Unit
) {
    val currentUserId = userInfo?.userId

    val claimsRepo = remember { ClaimsRepository(httpClient) }

    var loading by remember { mutableStateOf(true) }
    var actionError by remember { mutableStateOf<String?>(null) }
    var actionSuccess by remember { mutableStateOf<String?>(null) }

    // Manager data
    var todayAttendances by remember { mutableStateOf<List<AttendanceDto>>(emptyList()) }
    var weekSlots by remember { mutableStateOf<List<SlotDto>>(emptyList()) }
    var staffList by remember { mutableStateOf<List<StaffMemberDto>>(emptyList()) }
    var foodSafetyTemplates by remember { mutableStateOf<List<FoodSafetyTemplateDto>>(emptyList()) }
    var foodSafetyLogs by remember { mutableStateOf<List<FoodSafetyLogDto>>(emptyList()) }
    var pendingAbsences by remember { mutableStateOf<List<AbsenceRequestDto>>(emptyList()) }
    var unapprovedSlotCount by remember { mutableStateOf(0) }

    // Staff data
    var staffActiveAttendance by remember { mutableStateOf<AttendanceDto?>(null) }
    var staffNextSlot by remember { mutableStateOf<SlotDto?>(null) }
    var availableOpenSlots by remember { mutableStateOf<List<SlotDto>>(emptyList()) }
    var checkingAttendance by remember { mutableStateOf(false) }

    val coroutineScope = rememberCoroutineScope()

    fun loadData() {
        coroutineScope.launch {
            loading = true
            actionError = null
            try {
                val today = cafeTodayLocalDate()
                val monday = cafeTodayMondayOf(today)
                val sunday = cafeTodayAddDays(monday, 6)

                coroutineScope {
                    val attDef = async { attendanceRepo.getAttendance(workplaceId, today) }
                    val slotsDef = async { slotsRepo.getSlotsForRange(workplaceId, monday, sunday) }
                    val fsTmplDef = async { foodSafetyRepo.getTemplates(workplaceId) }
                    val fsLogsDef = async { foodSafetyRepo.getLogs(workplaceId, monday, sunday) }
                    val staffDef = async { staffRepo.getStaff(workplaceId) }

                    val attRes = attDef.await()
                    if (attRes.isSuccess) todayAttendances = attRes.getOrNull() ?: emptyList()

                    val slotsRes = slotsDef.await()
                    if (slotsRes.isSuccess) {
                        val allSlots = slotsRes.getOrNull() ?: emptyList()
                        weekSlots = allSlots
                        unapprovedSlotCount = allSlots.count { it.assignedToUserId == null && it.day <= today }
                        if (!isManager && currentUserId != null) {
                            staffNextSlot = allSlots.filter { it.assignedToUserId == currentUserId && it.day >= today }
                                .minByOrNull { "${it.day}T${it.startTime}" }
                            availableOpenSlots = allSlots.filter { it.assignedToUserId == null && it.needCoverage && it.day >= today }
                        }
                    }

                    val fsTmplRes = fsTmplDef.await()
                    if (fsTmplRes.isSuccess) foodSafetyTemplates = (fsTmplRes.getOrNull() ?: emptyList()).filter { it.active }

                    val fsLogsRes = fsLogsDef.await()
                    if (fsLogsRes.isSuccess) foodSafetyLogs = fsLogsRes.getOrNull() ?: emptyList()

                    val staffRes = staffDef.await()
                    if (staffRes.isSuccess) staffList = staffRes.getOrNull() ?: emptyList()

                    if (isManager) {
                        val absRes = absenceRepo.getAbsences(workplaceId)
                        if (absRes.isSuccess) {
                            pendingAbsences = (absRes.getOrNull() ?: emptyList()).filter { it.status == "pending" }
                        }
                    } else {
                        val myAttRes = attendanceRepo.getMyActiveAttendance(workplaceId)
                        if (myAttRes.isSuccess) staffActiveAttendance = myAttRes.getOrNull()
                    }
                }
            } catch (e: Exception) {
                actionError = "Kunde inte ladda dagens data: ${e.message}"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(workplaceId, isManager) {
        loadData()
    }

    val todayStr = cafeTodayLocalDate()
    val activeNow = todayAttendances.filter { it.checkOutTime == null }
    val todayScheduledSlots = weekSlots.filter { it.day == todayStr }

    // Quick temperature submission
    fun submitQuickTemp(tmpl: FoodSafetyTemplateDto, tempValue: Double) {
        coroutineScope.launch {
            actionError = null
            val req = CreateFoodSafetyLogRequest(
                templateId = tmpl.id,
                valueNumeric = tempValue,
                valueText = null,
                valueOk = null,
                deviationAction = null
            )
            val res = foodSafetyRepo.submitLog(workplaceId, req)
            if (res.isSuccess) {
                actionSuccess = "Loggade ${tempValue}°C för ${tmpl.name}"
                loadData()
            } else {
                actionError = "Kunde inte spara mätning: ${res.exceptionOrNull()?.message ?: "Okänt fel"}"
            }
        }
    }

    fun submitQuickRoutine(tmpl: FoodSafetyTemplateDto, ok: Boolean) {
        coroutineScope.launch {
            actionError = null
            val req = CreateFoodSafetyLogRequest(
                templateId = tmpl.id,
                valueNumeric = null,
                valueText = if (ok) "OK" else "Avvikelse",
                valueOk = ok,
                deviationAction = if (!ok) "Avvikelse noterad vid rutinkontroll" else null
            )
            val res = foodSafetyRepo.submitLog(workplaceId, req)
            if (res.isSuccess) {
                actionSuccess = "Signerade ${tmpl.name}"
                loadData()
            } else {
                actionError = "Kunde inte spara rutin: ${res.exceptionOrNull()?.message ?: "Okänt fel"}"
            }
        }
    }

    fun handleStaffCheckInOut() {
        if (checkingAttendance) return
        checkingAttendance = true
        coroutineScope.launch {
            actionError = null
            try {
                if (staffActiveAttendance == null) {
                    val slotId = staffNextSlot?.takeIf { it.day == todayStr }?.id
                    val res = attendanceRepo.checkIn(CheckInRequest(workplaceId = workplaceId, slotId = slotId))
                    if (res.isSuccess) {
                        actionSuccess = "Incheckad!"
                        loadData()
                    } else {
                        actionError = "Kunde inte stämpla in: ${res.exceptionOrNull()?.message ?: "Okänt fel"}"
                    }
                } else {
                    val attId = staffActiveAttendance!!.id
                    val res = attendanceRepo.checkOut(CheckOutRequest(attendanceId = attId))
                    if (res.isSuccess) {
                        actionSuccess = "Utstämplad! Bra jobbat idag."
                        loadData()
                    } else {
                        actionError = "Kunde inte stämpla ut: ${res.exceptionOrNull()?.message ?: "Okänt fel"}"
                    }
                }
            } finally {
                checkingAttendance = false
            }
        }
    }

    val greetingName = userInfo?.email?.substringBefore("@")
        ?.replace(".", " ")?.replace("-", " ")
        ?.split(" ")?.joinToString(" ") { it.replaceFirstChar { c -> c.uppercase() } }
        ?: if (isManager) "Caféägare" else "Medarbetare"

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1.5rem")
            property("padding-bottom", "3rem")
        }
    }) {
        // Welcome Header & Inspection Shortcuts
        Div({
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "flex-start")
                property("flex-wrap", "wrap")
                property("gap", "1rem")
                property("background", RosePineTheme.surface)
                property("border", "1px solid ${RosePineTheme.highlightMed}")
                property("border-radius", "16px")
                property("padding", "1.5rem")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
            }
        }) {
            Div {
                H2({
                    style {
                        property("font-family", "var(--font-serif, 'Newsreader', Georgia, serif)")
                        property("font-size", "1.75rem")
                        property("font-weight", "700")
                        property("color", RosePineTheme.text)
                        property("margin", "0 0 0.35rem 0")
                    }
                }) {
                    Text("God dag, $greetingName!")
                }
                P({
                    style {
                        property("font-size", "0.95rem")
                        property("color", RosePineTheme.subtle)
                        property("margin", "0")
                    }
                }) {
                    Text("Dagens läge · $todayStr")
                }
            }

            // Quick Access Buttons for Manager
            if (isManager) {
                Div({
                    style {
                        property("display", "flex")
                        property("gap", "0.75rem")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Button({
                        onClick { onNavigateToTab("LIGGARE") }
                        style {
                            property("min-height", "48px")
                            property("padding", "0.6rem 1.15rem")
                            property("background", RosePineTheme.surface)
                            property("border", "1px solid ${RosePineTheme.highlightHigh}")
                            property("border-radius", "12px")
                            property("color", RosePineTheme.text)
                            property("font-weight", "700")
                            property("font-size", "0.9rem")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-shield-halved", size = "1rem", color = RosePineTheme.pine)
                        Text("Skatteverket Kontrollvy")
                    }

                    Button({
                        onClick { onNavigateToTab("INSPEKTION") }
                        style {
                            property("min-height", "48px")
                            property("padding", "0.6rem 1.15rem")
                            property("background", RosePineTheme.surface)
                            property("border", "1px solid ${RosePineTheme.highlightHigh}")
                            property("border-radius", "12px")
                            property("color", RosePineTheme.text)
                            property("font-weight", "700")
                            property("font-size", "0.9rem")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-clipboard-check", size = "1rem", color = RosePineTheme.foam)
                        Text("Miljö & Hälsa PDF")
                    }
                }
            }
        }

        // Action Alerts
        actionError?.let { err ->
            Div({
                style {
                    property("background", RosePineTheme.errBg)
                    property("color", RosePineTheme.errText)
                    property("border", "1px solid ${RosePineTheme.errBorder}")
                    property("padding", "0.85rem 1.25rem")
                    property("border-radius", "12px")
                    property("font-weight", "600")
                    property("font-size", "0.95rem")
                }
            }) { Text(err) }
        }
        actionSuccess?.let { msg ->
            Div({
                style {
                    property("background", RosePineTheme.okBg)
                    property("color", RosePineTheme.okText)
                    property("border", "1px solid ${RosePineTheme.okBorder}")
                    property("padding", "0.85rem 1.25rem")
                    property("border-radius", "12px")
                    property("font-weight", "600")
                    property("font-size", "0.95rem")
                }
            }) { Text(msg) }
        }

        if (loading) {
            Div({
                style {
                    property("padding", "3rem")
                    property("text-align", "center")
                    property("color", RosePineTheme.subtle)
                    property("font-size", "1.1rem")
                }
            }) {
                Text("Laddar caféets läge för idag…")
            }
            return@Div
        }

        // ── MANAGER DASHBOARD VIEW ──
        if (isManager) {
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                    property("gap", "1.5rem")
                }
            }) {
                // CARD 1: Who is working now & scheduled today
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    Div({
                        style {
                            property("display", "flex")
                            property("justify-content", "space-between")
                            property("align-items", "center")
                        }
                    }) {
                        H3({
                            style {
                                property("font-size", "1.15rem")
                                property("font-weight", "800")
                                property("color", RosePineTheme.text)
                                property("margin", "0")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.5rem")
                            }
                        }) {
                            Span({
                                style {
                                    property("width", "12px")
                                    property("height", "12px")
                                    property("border-radius", "50%")
                                    property("background", if (activeNow.isNotEmpty()) RosePineTheme.foam else RosePineTheme.muted)
                                }
                            }) {}
                            Text("${activeNow.size} jobbar just nu")
                        }

                        Button({
                            onClick { onNavigateToTab("LIGGARE") }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                property("color", RosePineTheme.pine)
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                            }
                        }) { Text("Personalliggare →") }
                    }

                    if (activeNow.isEmpty()) {
                        P({ style { property("color", RosePineTheme.subtle); property("font-size", "0.9rem") } }) {
                            Text("Ingen personal är instämplad just nu.")
                        }
                    } else {
                        Div({
                            style {
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("gap", "0.5rem")
                            }
                        }) {
                            activeNow.forEach { att ->
                                val member = staffList.find { it.userId == att.userId }
                                val staffName = listOfNotNull(member?.firstName, member?.lastName).joinToString(" ").ifEmpty { member?.email ?: "Personal #${att.userId}" }
                                val checkInFormatted = att.checkInTime.substringAfter("T").take(5)
                                Div({
                                    style {
                                        property("display", "flex")
                                        property("justify-content", "space-between")
                                        property("align-items", "center")
                                        property("background", RosePineTheme.overlay)
                                        property("padding", "0.6rem 0.85rem")
                                        property("border-radius", "10px")
                                    }
                                }) {
                                    Span({ style { property("font-weight", "700"); property("color", RosePineTheme.text) } }) {
                                        Text(staffName)
                                    }
                                    Span({
                                        style {
                                            property("font-size", "0.85rem")
                                            property("color", RosePineTheme.okText)
                                            property("font-weight", "700")
                                            property("font-variant-numeric", "tabular-nums")
                                        }
                                    }) {
                                        Text("Inne sedan $checkInFormatted")
                                    }
                                }
                            }
                        }
                    }

                    // Scheduled today
                    Div({
                        style {
                            property("margin-top", "0.5rem")
                            property("border-top", "1px solid ${RosePineTheme.highlightLow}")
                            property("padding-top", "0.75rem")
                        }
                    }) {
                        P({
                            style {
                                property("font-size", "0.85rem")
                                property("font-weight", "700")
                                property("color", RosePineTheme.subtle)
                                property("margin", "0 0 0.5rem 0")
                            }
                        }) {
                            Text("Dagens ${todayScheduledSlots.size} schemalagda pass:")
                        }
                        if (todayScheduledSlots.isEmpty()) {
                            P({ style { property("font-size", "0.85rem"); property("color", RosePineTheme.muted) } }) {
                                Text("Inga pass schemalagda för idag.")
                            }
                        } else {
                            todayScheduledSlots.take(4).forEach { slot ->
                                val member = staffList.find { it.userId == slot.assignedToUserId }
                                val name = member?.let { listOfNotNull(it.firstName, it.lastName).joinToString(" ") } ?: "Ledigt pass"
                                Div({
                                    style {
                                        property("display", "flex")
                                        property("justify-content", "space-between")
                                        property("font-size", "0.85rem")
                                        property("padding", "0.25rem 0")
                                        property("color", RosePineTheme.text)
                                    }
                                }) {
                                    Span { Text(name) }
                                    Span({ style { property("font-variant-numeric", "tabular-nums"); property("font-weight", "600") } }) {
                                        Text("${slot.startTime.take(5)}–${slot.endTime.take(5)}")
                                    }
                                }
                            }
                        }
                    }
                }

                // CARD 2: Food Safety / HACCP Today with 3 Large Preset Buttons
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    Div({
                        style {
                            property("display", "flex")
                            property("justify-content", "space-between")
                            property("align-items", "center")
                        }
                    }) {
                        H3({
                            style {
                                property("font-size", "1.15rem")
                                property("font-weight", "800")
                                property("color", RosePineTheme.text)
                                property("margin", "0")
                            }
                        }) {
                            Text("Dagens kontroller")
                        }

                        Button({
                            onClick { onNavigateToTab("EGENKONTROLL") }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                property("color", RosePineTheme.pine)
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                            }
                        }) { Text("Alla kontroller →") }
                    }

                    if (foodSafetyTemplates.isEmpty()) {
                        P({ style { property("color", RosePineTheme.subtle); property("font-size", "0.9rem") } }) {
                            Text("Inga aktiva kontrollpunkter konfigurerade.")
                        }
                    } else {
                        Div({
                            style {
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("gap", "0.85rem")
                            }
                        }) {
                            foodSafetyTemplates.take(4).forEach { tmpl ->
                                val isDone = foodSafetyLogs.any { it.templateId == tmpl.id }
                                val lastLog = foodSafetyLogs.find { it.templateId == tmpl.id }

                                Div({
                                    style {
                                        property("border", "1px solid ${if (isDone) RosePineTheme.okBorder else RosePineTheme.highlightMed}")
                                        property("background", if (isDone) RosePineTheme.okBg else RosePineTheme.overlay)
                                        property("border-radius", "12px")
                                        property("padding", "0.85rem")
                                        property("display", "flex")
                                        property("flex-direction", "column")
                                        property("gap", "0.5rem")
                                    }
                                }) {
                                    Div({
                                        style {
                                            property("display", "flex")
                                            property("justify-content", "space-between")
                                            property("align-items", "center")
                                        }
                                    }) {
                                        Span({ style { property("font-weight", "700"); property("color", RosePineTheme.text) } }) {
                                            Text(tmpl.name)
                                        }
                                        if (isDone) {
                                            Span({
                                                style {
                                                    property("font-size", "0.8rem")
                                                    property("font-weight", "700")
                                                    property("color", RosePineTheme.okText)
                                                }
                                            }) {
                                                val logVal = lastLog?.valueNumeric?.let { "$it°C" } ?: lastLog?.valueText ?: "Klar ✓"
                                                Text("Loggad: $logVal")
                                            }
                                        }
                                    }

                                    // If not done, render the 3 large action buttons
                                    if (!isDone) {
                                        if (tmpl.category == "temperature") {
                                            val tMin = tmpl.minValue ?: 0.0
                                            val tMax = tmpl.maxValue ?: 8.0
                                            val mid = ((tMin + tMax) / 2.0 * 10).roundToInt() / 10.0
                                            val default3 = if (tMin < 0) {
                                                listOf(-18.0, -19.0, -20.0)
                                            } else {
                                                listOf(mid - 1.0, mid, mid + 1.0)
                                            }

                                            Div({
                                                style {
                                                    property("display", "flex")
                                                    property("gap", "0.5rem")
                                                }
                                            }) {
                                                default3.forEach { temp ->
                                                    Button({
                                                        onClick { submitQuickTemp(tmpl, temp) }
                                                        style {
                                                            property("flex", "1")
                                                            property("min-height", "48px")
                                                            property("background", RosePineTheme.surface)
                                                            property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                                            property("border-radius", "8px")
                                                            property("font-weight", "800")
                                                            property("font-size", "0.95rem")
                                                            property("color", RosePineTheme.pine)
                                                            property("cursor", "pointer")
                                                            property("font-variant-numeric", "tabular-nums")
                                                        }
                                                    }) {
                                                        val sign = if (temp > 0) "+$temp" else "$temp"
                                                        Text("$sign °C")
                                                    }
                                                }
                                            }
                                        } else {
                                            Button({
                                                onClick { submitQuickRoutine(tmpl, true) }
                                                style {
                                                    property("width", "100%")
                                                    property("min-height", "48px")
                                                    property("background", RosePineTheme.surface)
                                                    property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                                    property("border-radius", "8px")
                                                    property("font-weight", "800")
                                                    property("font-size", "0.95rem")
                                                    property("color", RosePineTheme.pine)
                                                    property("cursor", "pointer")
                                                    property("display", "inline-flex")
                                                    property("align-items", "center")
                                                    property("justify-content", "center")
                                                    property("gap", "0.5rem")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-check", size = "0.95rem", color = RosePineTheme.pine)
                                                Text("Allt OK — Signera")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // CARD 3: Payroll & Absences to Approve
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    H3({
                        style {
                            property("font-size", "1.15rem")
                            property("font-weight", "800")
                            property("color", RosePineTheme.text)
                            property("margin", "0")
                        }
                    }) {
                        Text("Att godkänna & granska")
                    }

                    // Unapproved shifts
                    Div({
                        style {
                            property("background", if (unapprovedSlotCount > 0) RosePineTheme.warnBg else RosePineTheme.okBg)
                            property("border", "1px solid ${if (unapprovedSlotCount > 0) RosePineTheme.warnBorder else RosePineTheme.okBorder}")
                            property("border-radius", "12px")
                            property("padding", "1rem")
                            property("display", "flex")
                            property("justify-content", "space-between")
                            property("align-items", "center")
                        }
                    }) {
                        Div {
                            Span({
                                style {
                                    property("display", "block")
                                    property("font-weight", "800")
                                    property("font-size", "1.1rem")
                                    property("color", if (unapprovedSlotCount > 0) RosePineTheme.warnText else RosePineTheme.okText)
                                }
                            }) {
                                Text("$unapprovedSlotCount arbetspass")
                            }
                            Span({
                                style {
                                    property("font-size", "0.85rem")
                                    property("color", if (unapprovedSlotCount > 0) RosePineTheme.warnText else RosePineTheme.okText)
                                }
                            }) {
                                Text(if (unapprovedSlotCount > 0) "Väntar på ditt godkännande inför lönekörning" else "Alla pass t.o.m. idag är godkända")
                            }
                        }

                        Button({
                            onClick { onNavigateToTab("LONER") }
                            style {
                                property("min-height", "44px")
                                property("padding", "0.5rem 1rem")
                                property("background", RosePineTheme.surface)
                                property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                property("border-radius", "8px")
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("color", RosePineTheme.text)
                                property("cursor", "pointer")
                            }
                        }) { Text("Granska →") }
                    }

                    // Pending absences
                    Div({
                        style {
                            property("background", if (pendingAbsences.isNotEmpty()) RosePineTheme.warnBg else RosePineTheme.overlay)
                            property("border", "1px solid ${if (pendingAbsences.isNotEmpty()) RosePineTheme.warnBorder else RosePineTheme.highlightMed}")
                            property("border-radius", "12px")
                            property("padding", "1rem")
                            property("display", "flex")
                            property("justify-content", "space-between")
                            property("align-items", "center")
                        }
                    }) {
                        Div {
                            Span({
                                style {
                                    property("display", "block")
                                    property("font-weight", "800")
                                    property("font-size", "1.1rem")
                                    property("color", if (pendingAbsences.isNotEmpty()) RosePineTheme.warnText else RosePineTheme.text)
                                }
                            }) {
                                Text("${pendingAbsences.size} frånvaroansökningar")
                            }
                            Span({
                                style {
                                    property("font-size", "0.85rem")
                                    property("color", if (pendingAbsences.isNotEmpty()) RosePineTheme.warnText else RosePineTheme.subtle)
                                }
                            }) {
                                Text(if (pendingAbsences.isNotEmpty()) "Semester/sjukanmälningar att godkänna" else "Inga väntande ledighetsansökningar")
                            }
                        }

                        Button({
                            onClick { onNavigateToTab("FRANVARO") }
                            style {
                                property("min-height", "44px")
                                property("padding", "0.5rem 1rem")
                                property("background", RosePineTheme.surface)
                                property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                property("border-radius", "8px")
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("color", RosePineTheme.text)
                                property("cursor", "pointer")
                            }
                        }) { Text("Hantera →") }
                    }
                }
            }
        }

        // ── STAFF DASHBOARD VIEW ──
        if (!isManager) {
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                    property("gap", "1.5rem")
                }
            }) {
                // STAFF CARD 1: My Shift & Big Check-In/Out Button
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1.25rem")
                    }
                }) {
                    H3({
                        style {
                            property("font-size", "1.15rem")
                            property("font-weight", "800")
                            property("color", RosePineTheme.text)
                            property("margin", "0")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.5rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-stopwatch", color = "var(--rp-pine)")
                        Text("Mitt arbetspass")
                    }

                    if (staffActiveAttendance != null) {
                        Div({
                            style {
                                property("background", RosePineTheme.okBg)
                                property("border", "1px solid ${RosePineTheme.okBorder}")
                                property("border-radius", "12px")
                                property("padding", "1rem")
                            }
                        }) {
                            Span({
                                style {
                                    property("font-weight", "800")
                                    property("color", RosePineTheme.okText)
                                    property("font-size", "1.1rem")
                                    property("display", "block")
                                }
                            }) {
                                Text("Du är instämplad")
                            }
                            Span({
                                style {
                                    property("font-size", "0.9rem")
                                    property("color", RosePineTheme.okText)
                                    property("font-variant-numeric", "tabular-nums")
                                }
                            }) {
                                Text("Sedan ${staffActiveAttendance!!.checkInTime.substringAfter("T").take(5)}")
                            }
                        }

                        Button({
                            onClick { handleStaffCheckInOut() }
                            style {
                                property("min-height", "56px")
                                property("background", RosePineTheme.love)
                                property("color", "#FFFFFF")
                                property("border", "none")
                                property("border-radius", "12px")
                                property("font-weight", "800")
                                property("font-size", "1.05rem")
                                property("cursor", "pointer")
                            }
                        }) {
                            Text("STÄMPLA UT")
                        }
                    } else {
                        val shiftInfo = staffNextSlot?.let { "Pass: ${it.day} kl. ${it.startTime.take(5)}–${it.endTime.take(5)}" } ?: "Inget inplanerat pass idag"
                        P({
                            style {
                                property("font-size", "1rem")
                                property("color", RosePineTheme.text)
                                property("font-weight", "600")
                                property("margin", "0")
                            }
                        }) { Text(shiftInfo) }

                        Button({
                            onClick { handleStaffCheckInOut() }
                            style {
                                property("min-height", "56px")
                                property("background", RosePineTheme.ctaBg)
                                property("color", RosePineTheme.ctaText)
                                property("border", "none")
                                property("border-radius", "12px")
                                property("font-weight", "800")
                                property("font-size", "1.05rem")
                                property("cursor", "pointer")
                            }
                        }) {
                            Text("STÄMPLA IN NU")
                        }
                    }
                }

                // STAFF CARD 2: Daily Controls for Staff to Tick Off
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    H3({
                        style {
                            property("font-size", "1.15rem")
                            property("font-weight", "800")
                            property("color", RosePineTheme.text)
                            property("margin", "0")
                        }
                    }) {
                        Text("Dagens kontroller")
                    }

                    foodSafetyTemplates.take(3).forEach { tmpl ->
                        val isDone = foodSafetyLogs.any { it.templateId == tmpl.id }
                        Div({
                            style {
                                property("border", "1px solid ${if (isDone) RosePineTheme.okBorder else RosePineTheme.highlightMed}")
                                property("background", if (isDone) RosePineTheme.okBg else RosePineTheme.overlay)
                                property("border-radius", "10px")
                                property("padding", "0.75rem")
                                property("display", "flex")
                                property("justify-content", "space-between")
                                property("align-items", "center")
                            }
                        }) {
                            Span({ style { property("font-weight", "700"); property("color", RosePineTheme.text) } }) {
                                Text(tmpl.name)
                            }

                            if (isDone) {
                                Span({ style { property("font-weight", "700"); property("color", RosePineTheme.okText) } }) {
                                    Text("Klar ✓")
                                }
                            } else {
                                Button({
                                    onClick { onNavigateToTab("EGENKONTROLL") }
                                    style {
                                        property("padding", "0.4rem 0.85rem")
                                        property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                        property("border-radius", "8px")
                                        property("background", RosePineTheme.surface)
                                        property("color", RosePineTheme.pine)
                                        property("font-weight", "700")
                                        property("cursor", "pointer")
                                    }
                                }) { Text("Logga nu") }
                            }
                        }
                    }
                }

                // STAFF CARD 3: Available shifts to claim
                Div({
                    style {
                        property("background", RosePineTheme.surface)
                        property("border", "1px solid ${RosePineTheme.highlightMed}")
                        property("border-radius", "16px")
                        property("padding", "1.5rem")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "1rem")
                    }
                }) {
                    H3({
                        style {
                            property("font-size", "1.15rem")
                            property("font-weight", "800")
                            property("color", RosePineTheme.text)
                            property("margin", "0")
                        }
                    }) {
                        Text("Lediga pass att ta")
                    }

                    if (availableOpenSlots.isEmpty()) {
                        P({ style { property("color", RosePineTheme.subtle); property("font-size", "0.9rem") } }) {
                            Text("Inga lediga pass utlagda just nu.")
                        }
                    } else {
                        availableOpenSlots.take(3).forEach { slot ->
                            Div({
                                style {
                                    property("border", "1px solid ${RosePineTheme.highlightMed}")
                                    property("border-radius", "10px")
                                    property("padding", "0.75rem")
                                    property("display", "flex")
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                }
                            }) {
                                Div {
                                    Span({ style { property("font-weight", "700"); property("display", "block") } }) {
                                        Text(slot.day)
                                    }
                                    Span({ style { property("font-size", "0.85rem"); property("color", RosePineTheme.subtle) } }) {
                                        Text("${slot.startTime.take(5)}–${slot.endTime.take(5)}")
                                    }
                                }

                                Button({
                                    onClick { onNavigateToTab("SCHEMA") }
                                    style {
                                        property("padding", "0.4rem 0.85rem")
                                        property("border", "1px solid ${RosePineTheme.highlightHigh}")
                                        property("border-radius", "8px")
                                        property("background", RosePineTheme.surface)
                                        property("color", RosePineTheme.pine)
                                        property("font-weight", "700")
                                        property("cursor", "pointer")
                                    }
                                }) { Text("Se pass") }
                            }
                        }
                    }
                }
            }
        }
    }
}
